import {
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text
} from '@chakra-ui/react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { MedicationAdministration } from '@/modules/medicalRecord/domain/models/FullPrescriptionPatient'
import { ResponsibleMedicalRecord } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/ResponsibleMedicalRecord'
import { ResponsibleMedicalRecordReview } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/ResponsibleMedicalRecordReview'
import { ResponsibleMedicalRecordView } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/ResponsibleMedicalRecordView'
import { ReviewResponsibleMedicalRecordForm } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/UpdateMedicalRecordDTO'
import { RemedyType } from '@/modules/remedies/domain/models/RemedyTypeEnum'
import { CustomSelect } from '@/ui/components/CustomSelect'

import { MedicalRecordReviewType } from '../../../../constants/MedicalRecordReviewType'
import { ReviewDataCheckboxArrayValue } from '../components/ReviewDataCheckbox'
import { useReviewFormRepeatedMultipleArrayValues } from '../components/ReviewFormRepeatedValue'
import ReviewStepWrapper from '../components/ReviewStepWrapper'

export function MedicationReviewStep({
  data,
  mode
}:
  | {
      data: ResponsibleMedicalRecordView
      mode: typeof MedicalRecordReviewType.enum.VIEW
    }
  | {
      data: ResponsibleMedicalRecordReview
      mode: typeof MedicalRecordReviewType.enum.REVIEW
    }) {
  const form = useFormContext<ReviewResponsibleMedicalRecordForm>()

  useReviewFormRepeatedMultipleArrayValues({
    disabled: mode === MedicalRecordReviewType.enum.VIEW,
    form,
    formProp: 'medications',
    serverValue: data.registerPatient.medications,
    responsibleValue: data.sendResponsible.medications,
    comparison: (a, b) =>
      !a
        ? false
        : !b
          ? false
          : a.medication === b.medication &&
            a.typeMedicine === b.typeMedicine &&
            a.activeIngredient === b.activeIngredient &&
            a.medicationAdministration === b.medicationAdministration
  })

  return (
    <Flex justifyContent={'space-between'} gap={'6'} overflowX={'clip'}>
      <MedicationReviewPart
        sourceData={data.sendResponsible}
        comparisonData={
          mode === 'REVIEW' ? data.registerPatient : form.getValues()
        }
        mode={mode}
      />
      <MedicationReviewPart
        sourceData={data.registerPatient}
        comparisonData={
          mode === 'REVIEW' ? data.sendResponsible : form.getValues()
        }
        mode={mode}
      />
    </Flex>
  )
}

function MedicationReviewPart({
  sourceData,
  comparisonData,
  mode
}: {
  sourceData: ResponsibleMedicalRecord
  comparisonData: ReviewResponsibleMedicalRecordForm | ResponsibleMedicalRecord
  mode: MedicalRecordReviewType
}) {
  const { t } = useTranslation()

  const form = useFormContext<ReviewResponsibleMedicalRecordForm>()
  const [medications] = form.watch(['medications'])
  const medicationsFieldArray = useFieldArray({
    control: form.control,
    name: 'medications'
  })

  return (
    <ReviewStepWrapper>
      {sourceData.medications.length === 0 ? (
        <Flex>
          <Text>Sem opções disponíveis</Text>
        </Flex>
      ) : (
        sourceData.medications
          .map((medication, index, { length }) => {
            return (
              <Flex
                key={medication.id}
                alignItems={'flex-start'}
                flexDir={'column'}
                gap={'4'}
              >
                <Text
                  color={'custom.gray.300'}
                  fontWeight={'400'}
                  fontSize={'1rem'}
                >
                  {index + 1} medicamento de {length}
                </Text>
                <ReviewDataCheckboxArrayValue
                  disabled={mode === MedicalRecordReviewType.enum.VIEW}
                  parallelValues={comparisonData.medications}
                  currentValue={medication}
                  formValue={medications}
                  set={(v) => medicationsFieldArray.append(v)}
                  reset={() =>
                    medicationsFieldArray.remove(
                      medicationsFieldArray.fields.findIndex(
                        (item) =>
                          item.activeIngredient ===
                            medication.activeIngredient &&
                          item.medication === medication.medication &&
                          item.medicationAdministration ===
                            medication.medicationAdministration &&
                          item.typeMedicine === medication.typeMedicine
                      ) ?? -1
                    )
                  }
                  comparison={(a, b) =>
                    a.activeIngredient === b.activeIngredient &&
                    a.medication === b.medication &&
                    a.medicationAdministration === b.medicationAdministration &&
                    a.typeMedicine === b.typeMedicine
                  }
                />
                <Flex alignItems={'flex-start'} gap={'4'} w={'full'}>
                  <FormControl>
                    <FormLabel>Medicamento</FormLabel>
                    <Input
                      value={medication.medication}
                      isDisabled
                      border={'solid 1px'}
                      borderColor={
                        medication.medication
                          ? 'custom.gray.400'
                          : 'custom.gray.600'
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Princípio ativo</FormLabel>
                    <Input
                      value={medication.activeIngredient}
                      isDisabled
                      border={'solid 1px'}
                      borderColor={
                        medication.activeIngredient
                          ? 'custom.gray.400'
                          : 'custom.gray.600'
                      }
                    />
                  </FormControl>
                </Flex>
                <Flex alignItems={'flex-start'} gap={'4'} w={'full'}>
                  <FormControl>
                    <FormLabel>Via de administração</FormLabel>
                    <CustomSelect
                      value={medication.medicationAdministration}
                      disabled
                      options={MedicationAdministration.options.map(
                        (medicationAdministration) => {
                          return {
                            label: t(
                              `admin.remedies.administrationRoute.${
                                medicationAdministration.toLowerCase() as Lowercase<
                                  typeof medicationAdministration
                                >
                              }`
                            ),
                            value: medicationAdministration
                          }
                        }
                      )}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Tipo</FormLabel>

                    <CustomSelect
                      value={medication.typeMedicine}
                      disabled
                      options={RemedyType.options.map((remedyType) => {
                        return {
                          label: t(
                            `admin.remedies.remedyType.${
                              remedyType.toLowerCase() as Lowercase<
                                typeof remedyType
                              >
                            }`
                          ),
                          value: remedyType
                        }
                      })}
                    />
                  </FormControl>
                </Flex>
              </Flex>
            )
          })
          .flatMap((item, i, { length }) =>
            // eslint-disable-next-line react/jsx-key
            i + 1 === length ? [item] : [item, <Divider />]
          )
      )}
    </ReviewStepWrapper>
  )
}
